import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { ProfessorNote } from '@/types'
import {
  Plus,
  X,
  Save,
  Loader2,
  StickyNote,
  Trash2,
  Pencil,
  Check
} from 'lucide-react'

// Contexto de ubicación computado
interface NoteContext {
  label: string
  unit_id: string | null
  lesson_id: string | null
  content_id: string | null
  exercise_id: string | null
}

// Extrae el UUID del pathname para no depender de useParams() en layouts padres
function extractIdFromPath(pathname: string, segment: string): string | null {
  const prefix = `/teacher/preview/${segment}/`
  if (!pathname.includes(prefix)) return null
  const id = pathname.split(prefix)[1]?.split('/')[0]
  return id || null
}

async function computeNoteContext(pathname: string): Promise<NoteContext> {
  const empty: NoteContext = { label: 'General', unit_id: null, lesson_id: null, content_id: null, exercise_id: null }

  const exerciseId = extractIdFromPath(pathname, 'exercise')
  if (exerciseId) {
    const { data } = await (supabase
      .from('exercises') as any)
      .select('id, order_num, content_id, content:contents(id, order_num, lesson:lessons(id, order_num, unit:units(id, order_num)))')
      .eq('id', exerciseId)
      .single()
    if (!data) return empty
    const c = data.content
    const l = c?.lesson
    const u = l?.unit

    return {
      label: `U${u?.order_num}-L${l?.order_num}-C${c?.order_num}-E${data.order_num}`,
      unit_id: u?.id || null,
      lesson_id: l?.id || null,
      content_id: c?.id || null,
      exercise_id: data.id
    }
  }

  const contentId = extractIdFromPath(pathname, 'content')
  if (contentId) {
    const { data } = await (supabase
      .from('contents') as any)
      .select('id, order_num, lesson:lessons(id, order_num, unit:units(id, order_num))')
      .eq('id', contentId)
      .single()
    if (!data) return empty
    const l = data.lesson
    const u = l?.unit
    return {
      label: `U${u?.order_num}-L${l?.order_num}-C${data.order_num}`,
      unit_id: u?.id || null,
      lesson_id: l?.id || null,
      content_id: data.id,
      exercise_id: null
    }
  }

  const lessonId = extractIdFromPath(pathname, 'lesson')
  if (lessonId) {
    const { data } = await (supabase
      .from('lessons') as any)
      .select('id, order_num, unit:units(id, order_num)')
      .eq('id', lessonId)
      .single()
    if (!data) return empty
    const u = data.unit
    return {
      label: `U${u?.order_num}-L${data.order_num}`,
      unit_id: u?.id || null,
      lesson_id: data.id,
      content_id: null,
      exercise_id: null
    }
  }

  const unitId = extractIdFromPath(pathname, 'unit')
  if (unitId) {
    const { data } = await (supabase
      .from('units') as any)
      .select('id, order_num')
      .eq('id', unitId)
      .single()
    if (!data) return empty
    return {
      label: `U${data.order_num}`,
      unit_id: data.id,
      lesson_id: null,
      content_id: null,
      exercise_id: null
    }
  }

  return empty
}

export default function SuperProfNoteButton() {
  const { isSuperProf, profile } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [ctx, setCtx] = useState<NoteContext | null>(null)
  const [loadingCtx, setLoadingCtx] = useState(false)
  const [notes, setNotes] = useState<ProfessorNote[]>([])
  const [loadingNotes, setLoadingNotes] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  const isPreviewRoute = location.pathname.startsWith('/teacher/preview/')

  // No mostrar si no es Super Prof o no está en ruta de preview
  if (!isSuperProf || !isPreviewRoute) return null

  const openModal = async () => {
    setOpen(true)
    setLoadingCtx(true)
    const c = await computeNoteContext(location.pathname)
    setCtx(c)
    setLoadingCtx(false)
    loadNotes(c)
  }

  const loadNotes = async (c: NoteContext) => {
    if (!profile) return
    setLoadingNotes(true)

    // Filtrar por el ID más específico disponible
    let query = (supabase.from('professor_notes') as any)
      .select('*')
      .eq('teacher_id', profile.id)
      .order('created_at', { ascending: false })

    if (c.exercise_id) query = query.eq('exercise_id', c.exercise_id)
    else if (c.content_id) query = query.eq('content_id', c.content_id).is('exercise_id', null)
    else if (c.lesson_id) query = query.eq('lesson_id', c.lesson_id).is('content_id', null).is('exercise_id', null)
    else if (c.unit_id) query = query.eq('unit_id', c.unit_id).is('lesson_id', null).is('content_id', null).is('exercise_id', null)
    else query = query.is('unit_id', null).is('lesson_id', null).is('content_id', null).is('exercise_id', null)

    const { data } = await query
    setNotes((data || []) as ProfessorNote[])
    setLoadingNotes(false)
  }

  const handleSave = async () => {
    if (!newNote.trim() || !ctx || !profile) return
    setSaving(true)
    setSaveError(null)
    const { error } = await (supabase.from('professor_notes') as any).insert({
      teacher_id: profile.id,
      unit_id: ctx.unit_id,
      lesson_id: ctx.lesson_id,
      content_id: ctx.content_id,
      exercise_id: ctx.exercise_id,
      location_label: ctx.label,
      note: newNote.trim()
    })
    if (error) {
      console.error('Error saving note:', error)
      setSaveError(`Error: ${error.message}`)
      setSaving(false)
      return
    }
    setNewNote('')
    await loadNotes(ctx)
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    await (supabase.from('professor_notes') as any).delete().eq('id', id)
    setNotes(notes.filter(n => n.id !== id))
  }

  const handleEdit = async (id: string) => {
    if (!editText.trim()) return
    await (supabase.from('professor_notes') as any).update({ note: editText.trim() }).eq('id', id)
    setNotes(notes.map(n => n.id === id ? { ...n, note: editText.trim() } : n))
    setEditingId(null)
    setEditText('')
  }

  return (
    <>
      {/* Botón flotante redondo */}
      <button
        onClick={openModal}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
        title="Agregar nota"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-gray-900">Notas del programa</span>
                {ctx && !loadingCtx && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-sm font-mono font-medium">
                    {ctx.label}
                  </span>
                )}
                {loadingCtx && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
              </div>
              <button
                onClick={() => { setOpen(false); setNewNote(''); setEditingId(null) }}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Notas existentes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingNotes ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : notes.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">No hay notas para esta ubicación</p>
              ) : (
                notes.map(note => (
                  <div key={note.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-amber-600 font-medium">
                        {new Date(note.created_at).toLocaleString('es-CL')}
                      </p>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => { setEditingId(note.id); setEditText(note.note) }}
                          className="p-1 hover:bg-amber-200 rounded text-amber-600"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {editingId === note.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditText('') }}
                            className="px-3 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleEdit(note.id)}
                            className="px-3 py-1 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Guardar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{note.note}</p>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Nueva nota */}
            <div className="p-4 border-t space-y-3">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                rows={3}
                placeholder="Escribe tu nota aquí..."
                className="w-full px-3 py-2 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              {saveError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</p>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving || !newNote.trim() || loadingCtx}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar nota
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
